/**
 * Terminal Rehitim — Premium RTL Hebrew Furniture Store Theme
 * Vanilla JS — No dependencies
 */

(function () {
  'use strict';

  /* ========================================================================
     Utility Helpers
     ======================================================================== */
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  function debounce(fn, delay = 100) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function trapFocus(container) {
    const focusable = $$('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ========================================================================
     1. Sticky Header
     ======================================================================== */
  function initStickyHeader() {
    const header = $('.header');
    if (!header) return;

    const announcement = $('.announcement-bar');
    let lastScroll = 0;
    const scrollThreshold = 50;

    function onScroll() {
      const currentScroll = window.scrollY;

      if (currentScroll > scrollThreshold) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      // Hide announcement bar on scroll
      if (announcement && !announcement.classList.contains('announcement-bar--dismissed')) {
        if (currentScroll > scrollThreshold) {
          header.classList.remove('has-announcement');
        } else {
          header.classList.add('has-announcement');
        }
      }

      lastScroll = currentScroll;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ========================================================================
     3. Announcement Bar Dismiss
     ======================================================================== */
  function initAnnouncementBar() {
    const bar = $('.announcement-bar');
    const dismiss = $('.announcement-bar__dismiss');
    const header = $('.header');
    if (!bar || !dismiss) return;

    dismiss.addEventListener('click', function () {
      bar.classList.add('announcement-bar--dismissed');
      if (header) header.classList.remove('has-announcement');
      try {
        sessionStorage.setItem('announcement-dismissed', 'true');
      } catch (e) { /* storage unavailable */ }
    });

    // Restore dismissed state
    try {
      if (sessionStorage.getItem('announcement-dismissed') === 'true') {
        bar.classList.add('announcement-bar--dismissed');
        if (header) header.classList.remove('has-announcement');
      }
    } catch (e) { /* storage unavailable */ }
  }

  /* ========================================================================
     4. Fade-up Animations on Scroll (IntersectionObserver)
     ======================================================================== */
  function initScrollAnimations() {
    const elements = $$('.fade-up, .fade-in');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ========================================================================
     5. Product Image Gallery
     ======================================================================== */
  function initProductGallery() {
    const mainImage = $('.product__gallery-main img');
    const thumbs = $$('.product__gallery-thumb');
    if (!mainImage || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const newSrc = this.dataset.src || this.querySelector('img').src;
        const newSrcset = this.dataset.srcset || '';
        const newAlt = this.dataset.alt || this.querySelector('img').alt || '';

        mainImage.src = newSrc;
        if (newSrcset) mainImage.srcset = newSrcset;
        mainImage.alt = newAlt;

        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
      });
    });
  }

  /* ========================================================================
     6. Quantity Selector
     ======================================================================== */
  function initQuantitySelectors() {
    $$('.product__quantity, .cart__item-quantity').forEach(function (wrapper) {
      const input = $('input', wrapper);
      const btnMinus = $('[data-action="decrease"]', wrapper);
      const btnPlus = $('[data-action="increase"]', wrapper);
      if (!input) return;

      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 9999;

      function update(newVal) {
        const clamped = Math.min(Math.max(newVal, min), max);
        input.value = clamped;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (btnMinus) {
        btnMinus.addEventListener('click', function () {
          update(parseInt(input.value, 10) - 1);
        });
      }

      if (btnPlus) {
        btnPlus.addEventListener('click', function () {
          update(parseInt(input.value, 10) + 1);
        });
      }

      input.addEventListener('change', function () {
        const val = parseInt(this.value, 10);
        if (isNaN(val) || val < min) this.value = min;
        else if (val > max) this.value = max;
      });
    });
  }

  /* ========================================================================
     8. Accordion
     ======================================================================== */
  function initAccordions() {
    $$('.product__accordion-trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        const item = this.closest('.product__accordion-item');
        const content = item.querySelector('.product__accordion-content');
        if (!content) return;

        const isOpen = item.classList.contains('is-open');

        // Close all siblings
        const parent = item.parentElement;
        $$('.product__accordion-item', parent).forEach(function (sibling) {
          sibling.classList.remove('is-open');
          const sibContent = sibling.querySelector('.product__accordion-content');
          if (sibContent) sibContent.style.maxHeight = null;
        });

        // Toggle current
        if (!isOpen) {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  /* ========================================================================
     9. Lightbox
     ======================================================================== */
  function initLightbox() {
    const lightbox = $('.lightbox');
    if (!lightbox) return;

    const image = $('.lightbox__image', lightbox);
    const closeBtn = $('.lightbox__close', lightbox);
    const prevBtn = $('.lightbox__nav--prev', lightbox);
    const nextBtn = $('.lightbox__nav--next', lightbox);
    const counter = $('.lightbox__counter', lightbox);
    let items = [];
    let currentIndex = 0;

    function open(src, index) {
      if (image) image.src = src;
      currentIndex = index;
      updateCounter();
      lightbox.classList.add('is-open');
      document.body.classList.add('no-scroll');
    }

    function close() {
      lightbox.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }

    function navigate(direction) {
      currentIndex = (currentIndex + direction + items.length) % items.length;
      const src = items[currentIndex].dataset.lightboxSrc || items[currentIndex].querySelector('img').src;
      if (image) image.src = src;
      updateCounter();
    }

    function updateCounter() {
      if (counter) counter.textContent = (currentIndex + 1) + ' / ' + items.length;
    }

    items = $$('[data-lightbox]');
    items.forEach(function (item, index) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        const src = this.dataset.lightboxSrc || this.querySelector('img').src;
        open(src, index);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { navigate(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { navigate(1); });

    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) close();
    });

    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') navigate(-1); // RTL: right = prev
      if (e.key === 'ArrowLeft') navigate(1);   // RTL: left = next
    });
  }

  /* ========================================================================
     10. Smooth Scroll for Anchor Links
     ======================================================================== */
  function initSmoothScroll() {
    $$('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#' || targetId.length < 2) return;
        const target = $(targetId);
        if (!target) return;
        e.preventDefault();
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 72;
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  /* ========================================================================
     11. Sort Dropdown
     ======================================================================== */
  function initSortDropdown() {
    $$('.sort-by').forEach(function (sortContainer) {
      const trigger = $('.sort-by__trigger', sortContainer);
      if (!trigger) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        sortContainer.classList.toggle('is-open');
      });

      $$('.sort-by__option', sortContainer).forEach(function (option) {
        option.addEventListener('click', function () {
          $$('.sort-by__option', sortContainer).forEach(function (o) { o.classList.remove('is-active'); });
          this.classList.add('is-active');
          const label = $('.sort-by__trigger-label', sortContainer);
          if (label) label.textContent = this.textContent;
          sortContainer.classList.remove('is-open');
        });
      });

      document.addEventListener('click', function () {
        sortContainer.classList.remove('is-open');
      });
    });
  }

  /* ========================================================================
     12. Product Card Quick Add (AJAX)
     ======================================================================== */
  function initQuickAdd() {
    $$('.product-card__quick-add-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const variantId = this.dataset.variantId;
        if (!variantId) return;

        this.classList.add('is-loading');
        this.disabled = true;

        const formData = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })
          .then(function (response) { return response.json(); })
          .then(function () {
            btn.classList.remove('is-loading');
            btn.disabled = false;
            // Update cart count
            updateCartCount();
            var countEl = document.querySelector('[data-cart-count]');
            if (countEl) {
              fetch('/cart.json').then(function(r) { return r.json(); }).then(function(cart) {
                countEl.textContent = cart.item_count;
              });
            }
            // Show toast notification
            var toast = document.createElement('div');
            toast.textContent = '\u05D4\u05DE\u05D5\u05E6\u05E8 \u05E0\u05D5\u05E1\u05E3 \u05DC\u05E1\u05DC';
            toast.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#1a1a1a;color:#fff;padding:12px 32px;border-radius:9999px;font-size:14px;z-index:99999;direction:rtl;box-shadow:0 4px 16px rgba(0,0,0,0.15);transition:opacity 0.3s;';
            document.body.appendChild(toast);
            setTimeout(function(){ toast.style.opacity='0'; }, 2500);
            setTimeout(function(){ toast.remove(); }, 3000);
            // Redirect to cart if not drawer type
            if (document.body.dataset.cartType !== 'drawer') {
              setTimeout(function(){ window.location.href = '/cart'; }, 800);
            }
          })
          .catch(function () {
            btn.classList.remove('is-loading');
            btn.disabled = false;
          });
      });
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        $$('.header__cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = 'flex';
          } else {
            el.style.display = 'none';
          }
        });
      })
      .catch(function () { /* silent fail */ });
  }

  /* ========================================================================
     13. Product Page Form / Add to Cart
     ======================================================================== */
  function initProductForm() {
    const form = $('form[data-product-form]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      // If cart type is drawer, intercept and add via AJAX
      if (document.body.dataset.cartType !== 'drawer') return;
      e.preventDefault();

      const submitBtn = $('[type="submit"]', form);
      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
      }

      const formData = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
        .then(function (response) { return response.json(); })
        .then(function () {
          if (submitBtn) {
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
          }
          updateCartCount();
          var countEl = document.querySelector('[data-cart-count]');
          if (countEl) {
            fetch('/cart.json').then(function(r) { return r.json(); }).then(function(cart) {
              countEl.textContent = cart.item_count;
            });
          }
          // Show toast notification
          var toast = document.createElement('div');
          toast.textContent = '\u05D4\u05DE\u05D5\u05E6\u05E8 \u05E0\u05D5\u05E1\u05E3 \u05DC\u05E1\u05DC';
          toast.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#1a1a1a;color:#fff;padding:12px 32px;border-radius:9999px;font-size:14px;z-index:99999;direction:rtl;box-shadow:0 4px 16px rgba(0,0,0,0.15);transition:opacity 0.3s;';
          document.body.appendChild(toast);
          setTimeout(function(){ toast.style.opacity='0'; }, 2500);
          setTimeout(function(){ toast.remove(); }, 3000);
          // Redirect to cart if not drawer type
          if (document.body.dataset.cartType !== 'drawer') {
            setTimeout(function(){ window.location.href = '/cart'; }, 800);
          }
        })
        .catch(function () {
          if (submitBtn) {
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
          }
        });
    });
  }

  /* ========================================================================
     14. Lazy Load Images
     ======================================================================== */
  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) return; // native support

    const images = $$('img[loading="lazy"]');
    if (!images.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          observer.unobserve(img);
        }
      });
    });

    images.forEach(function (img) { observer.observe(img); });
  }

  /* ========================================================================
     Initialize Everything on DOMContentLoaded
     ======================================================================== */
  document.addEventListener('DOMContentLoaded', function () {
    initStickyHeader();
    initAnnouncementBar();
    initScrollAnimations();
    initProductGallery();
    initQuantitySelectors();
    initAccordions();
    initLightbox();
    initSmoothScroll();
    initSortDropdown();
    initQuickAdd();
    initProductForm();
    initLazyLoad();
  });
})();
